import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, forwardRef, inject, NgZone, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, EMPTY, forkJoin, Observable, of, Subject, switchMap, tap } from 'rxjs';
import CalendarUiComponent from '@iupics-components/standard/fields/calendar-ui/calendar-ui.component';
import { IupicsDataField } from '@iupics-manager/models/iupics-data';
import SpecificWindowUiComponent from '../specific-window-ui/specific-window-ui.component';
import { AuditChangeDetailComponent } from './audit-change-detail.component';
import { AuditDocumentDetailComponent } from './audit-document-detail.component';
import { AuditExecutionDetailComponent } from './audit-execution-detail.component';
import { AuditFormat } from './audit-format';
import { AuditOptionPickerComponent } from './audit-option-picker.component';
import {
  activeFilterKeys,
  AuditFilterKey,
  AuditFilters,
  AuditPeriod,
  AuditTab,
  clearAllFilters,
  clearFilter,
  defaultFilters,
  DOC_ACTIONS,
  executionChangesWindow,
  formatDuration,
  fromQueryParams,
  isStale,
  PAGE_SIZES,
  PERIODS,
  periodRange,
  readableRecordId,
  toApiParams,
  toLocalDay,
  toLocalDateTime,
  parseLocalMoment,
  toQueryParams,
} from './audit-query';
import {
  AuditChange,
  AuditDocAction,
  AuditDocumentAccess,
  AuditExecution,
  AuditExecutionDetail,
  AuditExecutionStatus,
  AuditOperation,
  AuditOption,
  AuditOptionKind,
  AuditPage,
  AuditSummary,
} from './audit.models';
import { AuditService } from './audit.service';

type LoadState = 'loading' | 'ready' | 'error';

/** Live refresh while executions run (ms) */
const LIVE_REFRESH = 5000;

/**
 * Audit & monitoring dashboard (AD_Form "Audit_Monitoring"): process executions (AD_PInstance) and record changes
 * (MongoDB changelogs) of a period, filtered and paged server-side. The filters live in the URL query string of the
 * form, so a view can be bookmarked or shared (the backend applies the access of whoever opens it).
 */
@Component({
  selector: 'iu-audit-window-ui',
  standalone: true,
  // forwardRef: calendar-ui is part of an import cycle with the component registry (undefined at definition time)
  imports: [TranslateModule, forwardRef(() => CalendarUiComponent), AuditOptionPickerComponent, AuditExecutionDetailComponent, AuditChangeDetailComponent, AuditDocumentDetailComponent],
  templateUrl: './audit-window-ui.component.html',
  styleUrls: ['./audit-window-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class AuditWindowUiComponent extends SpecificWindowUiComponent implements OnInit, OnDestroy {
  readonly #audit = inject(AuditService);
  readonly #location = inject(Location);
  readonly #destroyRef = inject(DestroyRef);
  readonly #cd = inject(ChangeDetectorRef);
  readonly #zone = inject(NgZone);

  /** Apiz date-time field (iu-calendar-ui) for the custom period */
  readonly timeField = { needTime: true } as IupicsDataField;
  readonly locale: string = this.connectorService.getIupicsUserAccount()?.default_language?.iso_code;
  /** stop of a running process: administrators, as in the "Process in progress" window */
  readonly canStop: boolean = !!this.connectorService.getIupicsUserAccount()?.current_role?.isAdministrator;
  readonly stoppingId = signal<number | null>(null);
  /** outcome of the last stop when it did not end the run (message key or server message) */
  readonly stopNote = signal<{ id: number; note: string } | null>(null);
  #stopTimer: ReturnType<typeof setTimeout> | undefined;
  readonly periods = PERIODS;
  readonly pageSizes = PAGE_SIZES;
  readonly statuses: AuditExecutionStatus[] = ['R', 'S', 'F', 'C'];
  readonly operations: AuditOperation[] = ['I', 'U', 'D', 'STUDIO'];
  readonly docActions = DOC_ACTIONS;

  readonly query = signal<AuditFilters>(defaultFilters());
  readonly now = signal(new Date());
  readonly fmt = signal(new AuditFormat(this.translateService.currentLang));

  readonly summary = signal<AuditSummary | null>(null);
  readonly summaryState = signal<LoadState>('loading');
  readonly executions = signal<AuditPage<AuditExecution> | null>(null);
  readonly changes = signal<AuditPage<AuditChange> | null>(null);
  readonly documents = signal<AuditPage<AuditDocumentAccess> | null>(null);
  readonly listState = signal<LoadState>('loading');
  readonly listError = signal<string | null>(null);
  readonly live = signal(true);
  readonly exporting = signal(false);

  /** names of the selected process / user / table (chips, pickers) */
  readonly names = signal<Record<string, string>>({});

  /** detail panel */
  readonly selectedExecution = signal<AuditExecution | null>(null);
  readonly executionDetail = signal<AuditExecutionDetail | null>(null);
  readonly selectedChange = signal<AuditChange | null>(null);
  readonly selectedAccess = signal<AuditDocumentAccess | null>(null);
  readonly detailLoading = signal(false);
  readonly detailError = signal<string | null>(null);

  readonly tab = computed(() => this.query().tab);
  readonly range = computed(() => periodRange(this.query(), this.now()));
  readonly activeKeys = computed(() => activeFilterKeys(this.query()));
  readonly page = computed<AuditPage<unknown> | null>(() =>
    this.tab() === 'executions' ? this.executions() : this.tab() === 'changes' ? this.changes() : this.documents()
  );
  readonly pageInfo = computed(() => {
    const p = this.page();
    if (!p || p.total === 0) return null;
    return { from: p.offset + 1, to: Math.min(p.offset + p.items.length, p.total), total: p.total };
  });
  readonly hasNext = computed(() => {
    const p = this.page();
    return !!p && p.offset + p.items.length < p.total;
  });
  readonly anyRunning = computed(
    () => (this.summary()?.runningNow ?? 0) > 0 || (this.executions()?.items ?? []).some((e) => e.status === 'R')
  );
  readonly detailOpen = computed(() => !!this.selectedExecution() || !!this.selectedChange() || !!this.selectedAccess());

  readonly #load$ = new Subject<{ list: boolean; summary: boolean; silent?: boolean }>();
  readonly #search$ = new Subject<string>();
  readonly #detail$ = new Subject<{ kind: 'execution' | 'change'; id: number | string; silent?: boolean }>();
  #timer: ReturnType<typeof setInterval> | null = null;

  duration = formatDuration;
  recordLabel = readableRecordId;

  constructor() {
    super();
    this.#load$
      .pipe(
        switchMap(({ list, summary, silent }) => {
          const f = this.query();
          this.now.set(new Date());
          const params = toApiParams(f, this.now());
          if (!silent) {
            if (list) this.listState.set('loading');
            if (summary) this.summaryState.set('loading');
          }
          const fetched$: Observable<unknown> =
            f.tab === 'executions'
              ? this.#audit.executions(params).pipe(tap((page: AuditPage<AuditExecution>) => this.executions.set(page)))
              : f.tab === 'changes'
                ? this.#audit.changes(params).pipe(tap((page: AuditPage<AuditChange>) => this.changes.set(page)))
                : this.#audit.documents(params).pipe(tap((page: AuditPage<AuditDocumentAccess>) => this.documents.set(page)));
          // a page past the end (old link, fewer results since): go to the last page
          const clamped$ = fetched$.pipe(
            tap((page) => {
              const p = page as AuditPage<unknown>;
              if (p.items.length === 0 && p.total > 0 && f.page > 0)
                setTimeout(() => this.setPage(Math.floor((p.total - 1) / f.pageSize)));
            })
          );
          const list$: Observable<unknown> = !list
            ? of(null)
            : clamped$.pipe(
                tap(() => {
                  this.listState.set('ready');
                  this.listError.set(null);
                }),
                catchError((e) => {
                  if (!silent || this.listState() !== 'ready') {
                    this.listState.set('error');
                    this.listError.set(this.#message(e));
                  }
                  return of(null);
                })
              );
          const summary$: Observable<unknown> = !summary
            ? of(null)
            : this.#audit.summary(params['from'], params['to'], !!silent).pipe(
                tap((s: AuditSummary) => {
                  const prev = this.summary();
                  // live refresh: executions only, the record change part is kept
                  this.summary.set(
                    s.changes < 0 && prev
                      ? { ...s, changes: prev.changes, inserts: prev.inserts, updates: prev.updates, deletes: prev.deletes, changesSince: prev.changesSince, executionsSince: prev.executionsSince, documentReads: prev.documentReads, documentDownloads: prev.documentDownloads }
                      : s
                  );
                  this.summaryState.set('ready');
                }),
                catchError(() => {
                  if (!silent) this.summaryState.set('error');
                  return of(null);
                })
              );
          return forkJoin([list$, summary$]);
        }),
        takeUntilDestroyed()
      )
      .subscribe(() => this.cdr());

    this.#search$.pipe(debounceTime(350), takeUntilDestroyed()).subscribe((q) => this.patch({ q }, true));

    this.#detail$
      .pipe(
        switchMap(({ kind, id, silent }) => {
          if (!silent) {
            this.detailLoading.set(true);
            this.detailError.set(null);
          }
          // answers for a row that is no longer selected are dropped
          const req$: Observable<unknown> =
            kind === 'execution'
              ? this.#audit.execution(id as number).pipe(
                  tap((d: AuditExecutionDetail) => {
                    if (this.selectedExecution()?.id === id) this.executionDetail.set(d);
                  })
                )
              : this.#audit.change(id as string).pipe(
                  tap((c: AuditChange) => {
                    if (this.selectedChange()?.id === id) this.selectedChange.set(c);
                  })
                );
          return req$.pipe(
            tap(() => this.detailLoading.set(false)),
            catchError((e) => {
              this.detailLoading.set(false);
              if (!silent) this.detailError.set(this.#message(e));
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe(() => this.cdr());
  }

  override ngOnInit(): void {
    // custom screen: no form fields to build (the base ngOnInit would create the form store and build them)
    if (!this.isModal && this.activeTab) {
      this.workspaceService.addLinkedComponentToTabMap(this.activeTab.id, this);
    }
    this.query.set(fromQueryParams(new URLSearchParams(window.location.search)));
    this.#resolveNames();
    this.#load$.next({ list: true, summary: true });
    // ticks outside Angular: enter (and run change detection) only while the audit tab is on screen
    this.#timer = this.#zone.runOutsideAngular(() =>
      setInterval(() => {
        if (document.visibilityState === 'visible' && this.#isVisible()) this.#zone.run(() => this.#liveTick());
      }, LIVE_REFRESH)
    );
  }

  override ngAfterViewInit(): void {
    // nothing to wire: the base one only registers the tab (done in ngOnInit)
  }

  override ngOnDestroy(): void {
    if (this.#timer) clearInterval(this.#timer);
    clearTimeout(this.#stopTimer);
    super.ngOnDestroy();
  }

  /** the base builds the AD_FormDetail fields: this screen has none */
  override buildWindow(): void {}

  #liveTick(): void {
    this.now.set(new Date());
    if (!this.live() || document.visibilityState !== 'visible' || !this.anyRunning() || this.listState() === 'loading') {
      this.cdr();
      return;
    }
    if (!this.#isVisible()) return;
    this.#load$.next({ list: this.tab() === 'executions', summary: true, silent: true });
    const sel = this.selectedExecution();
    if (sel && (this.executionDetail()?.execution.status ?? sel.status) === 'R')
      this.#detail$.next({ kind: 'execution', id: sel.id, silent: true });
  }

  /** the audit tab is the one on screen (several workspace tabs can be open) */
  #isVisible(): boolean {
    return !!this.specificContainer?.nativeElement?.offsetParent;
  }

  //#region filters
  patch(change: Partial<AuditFilters>, resetPage = true): void {
    this.query.update((f) => ({ ...f, ...change, page: resetPage ? 0 : (change.page ?? f.page) }));
    this.#syncUrl();
    this.#load$.next({ list: true, summary: 'period' in change || 'from' in change || 'to' in change });
  }

  setTab(tab: AuditTab): void {
    if (tab === this.tab()) return;
    this.closeDetail();
    this.patch({ tab });
  }

  setPeriod(period: AuditPeriod): void {
    if (period === 'custom') {
      const { from, to } = this.range();
      this.patch({ period, from: toLocalDateTime(from), to: toLocalDateTime(to) });
    } else this.patch({ period, from: null, to: null });
  }

  /** custom bounds as dates for the calendar fields */
  readonly customFrom = computed(() => parseLocalMoment(this.query().from)?.date ?? null);
  readonly customTo = computed(() => {
    const end = parseLocalMoment(this.query().to);
    return end ? (end.dayOnly ? new Date(end.date.getFullYear(), end.date.getMonth(), end.date.getDate() + 1) : end.date) : null;
  });

  /** a bound picked in the calendar (minute precision); ignored when it would make an empty period */
  setCustomBound(which: 'from' | 'to', value: Date | null): void {
    if (!(value instanceof Date) || isNaN(value.getTime())) return;
    const { from, to } = this.range();
    const nextFrom = which === 'from' ? value : from;
    const nextTo = which === 'to' ? value : to;
    if (nextFrom >= nextTo) return;
    this.patch({ period: 'custom', from: toLocalDateTime(nextFrom), to: toLocalDateTime(nextTo) });
  }

  onSearch(value: string): void {
    this.#search$.next(value);
  }

  toggleStatus(status: AuditExecutionStatus): void {
    const s = this.query().status;
    this.patch({ status: s.includes(status) ? s.filter((x) => x !== status) : [...s, status] });
  }

  toggleOperation(op: AuditOperation): void {
    const s = this.query().operation;
    this.patch({ operation: s.includes(op) ? s.filter((x) => x !== op) : [...s, op] });
  }

  toggleAction(action: AuditDocAction): void {
    const s = this.query().action;
    this.patch({ action: s.includes(action) ? s.filter((x) => x !== action) : [...s, action] });
  }

  /** reads of one document (its id as search) */
  documentReads(a: AuditDocumentAccess): void {
    this.closeDetail();
    this.patch({ ...clearAllFilters(this.query()), tab: 'documents', q: a.docId });
  }

  /** summary card: document access list */
  showDocuments(action: AuditDocAction[]): void {
    this.closeDetail();
    this.patch({ ...clearAllFilters(this.query()), tab: 'documents', action });
  }

  /** second line of a document row: how it was read ("Documents window - v2") */
  docChannelLabel(a: AuditDocumentAccess): string {
    const channel = this.translateService.instant(`audit.docChannelLong.${a.channel || 'app'}`);
    return a.version > 0 ? `${channel} - v${a.version}` : channel;
  }

  openAccess(a: AuditDocumentAccess): void {
    this.selectedExecution.set(null);
    this.executionDetail.set(null);
    this.selectedChange.set(null);
    this.selectedAccess.set(a);
    this.detailLoading.set(false);
    this.detailError.set(null);
    this.#focusDetail();
  }

  setType(value: string): void {
    this.patch({ type: value === 'P' || value === 'R' ? value : null });
  }

  setRecordId(value: string): void {
    const recordId = value.trim() || null;
    if (recordId !== this.query().recordId) this.patch({ recordId });
  }

  pickOption(kind: AuditOptionKind, option: AuditOption): void {
    this.names.update((n) => ({ ...n, [`${kind}:${option.id}`]: option.name }));
    if (kind === 'process') this.patch({ processId: option.id });
    else if (kind === 'user') this.patch({ userId: option.id });
    else this.patch({ tableId: option.id });
  }

  nameOf(kind: AuditOptionKind, id: number | null): string {
    return id ? (this.names()[`${kind}:${id}`] ?? `#${id}`) : '';
  }

  removeFilter(key: AuditFilterKey): void {
    this.query.set(clearFilter(this.query(), key));
    this.#syncUrl();
    this.#load$.next({ list: true, summary: false });
  }

  clearFilters(): void {
    this.query.set(clearAllFilters(this.query()));
    this.#syncUrl();
    this.#load$.next({ list: true, summary: false });
  }

  setPage(page: number): void {
    this.patch({ page: Math.max(0, page) }, false);
  }

  setPageSize(size: string): void {
    this.patch({ pageSize: Number(size) });
  }

  toggleSort(): void {
    this.patch({ sort: this.query().sort === 'desc' ? 'asc' : 'desc' });
  }

  /** summary cards: shortcut to the matching list */
  showExecutions(status: AuditExecutionStatus[]): void {
    this.closeDetail();
    this.patch({ ...clearAllFilters(this.query()), tab: 'executions', status });
  }

  showChanges(operation: AuditOperation[]): void {
    this.closeDetail();
    this.patch({ ...clearAllFilters(this.query()), tab: 'changes', operation });
  }

  reload(): void {
    this.#load$.next({ list: true, summary: true });
  }

  /** filters in the URL query string of this form (bookmark / share); only while this form owns the URL */
  #syncUrl(): void {
    const path = this.#location.path().split('?')[0];
    if (!path.startsWith('/workspace/form/')) return;
    this.#location.replaceState(path, toQueryParams(this.query()).toString());
  }

  /** names of the ids read from the URL */
  #resolveNames(): void {
    const f = this.query();
    const wanted: [AuditOptionKind, number | null][] = [
      ['process', f.processId],
      ['user', f.userId],
      ['table', f.tableId],
    ];
    for (const [kind, id] of wanted) {
      if (!id) continue;
      this.#audit
        .options(kind, null, [id])
        .pipe(catchError(() => of([] as AuditOption[])), takeUntilDestroyed(this.#destroyRef))
        .subscribe((opts) => {
          if (opts[0]) this.names.update((n) => ({ ...n, [`${kind}:${id}`]: opts[0].name }));
          this.cdr();
        });
    }
  }
  //#endregion

  //#region detail
  openExecution(e: AuditExecution): void {
    this.selectedChange.set(null);
    this.selectedExecution.set(e);
    this.executionDetail.set(null);
    this.#detail$.next({ kind: 'execution', id: e.id });
    this.#focusDetail();
  }

  /** sub-run of the open execution, or the root run of a sub-run (not in the list: lists show root runs) */
  openRun(id: number): void {
    const known = this.executions()?.items.find((x) => x.id === id) ?? this.executionDetail()?.children.find((x) => x.id === id);
    if (known) {
      this.openExecution(known);
      return;
    }
    this.detailLoading.set(true);
    this.detailError.set(null);
    this.#audit
      .execution(id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (d) => {
          this.selectedChange.set(null);
          this.selectedExecution.set(d.execution);
          this.executionDetail.set(d);
          this.detailLoading.set(false);
          this.cdr();
        },
        error: (e) => {
          this.detailLoading.set(false);
          this.detailError.set(this.#message(e));
          this.cdr();
        },
      });
  }

  /** move the focus into the detail panel (keyboard users, Escape to close) */
  #focusDetail(): void {
    setTimeout(() => (this.specificContainer?.nativeElement as HTMLElement)?.querySelector<HTMLElement>('.audit-detail .aud-close')?.focus());
  }

  openChange(c: AuditChange): void {
    this.selectedExecution.set(null);
    this.executionDetail.set(null);
    // the list row already carries the change (fields included): nothing to load
    this.selectedChange.set(c);
    this.detailLoading.set(false);
    this.detailError.set(null);
    this.#focusDetail();
  }

  retryDetail(): void {
    const e = this.selectedExecution();
    if (e) this.#detail$.next({ kind: 'execution', id: e.id });
    const c = this.selectedChange();
    if (c) this.#detail$.next({ kind: 'change', id: c.id });
  }

  closeDetail(): void {
    this.selectedExecution.set(null);
    this.executionDetail.set(null);
    this.selectedChange.set(null);
    this.selectedAccess.set(null);
    this.detailError.set(null);
  }

  onRowKey(event: KeyboardEvent, row: AuditExecution | AuditChange | AuditDocumentAccess): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if ('processId' in row) this.openExecution(row);
    else if ('action' in row) this.openAccess(row);
    else this.openChange(row);
  }

  /** server side stop (POST executions/{id}/stop), then follow the execution until it is no longer running */
  stopExecution(e: AuditExecution): void {
    this.stoppingId.set(e.id);
    this.stopNote.set(null);
    this.#audit
      .stopExecution(e.id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: ({ result }) => {
          if (result === 'NOT_FOUND') this.#stopEnded(e.id, 'audit.execution.stopNotFound');
          else if (result === 'NOT_CANCELLABLE') this.#stopEnded(e.id, 'audit.execution.stopNotCancellable');
          else if (result === 'NOT_RUNNING') this.#stopEnded(e.id, null);
          else this.#followStop(e.id, 0);
        },
        error: (err: HttpErrorResponse) => this.#stopEnded(e.id, err?.error?.message || err?.message || 'audit.execution.stopFailed'),
      });
  }

  /** poll the execution (1.5 s, 20 times): a stop takes effect when the run reaches its next SQL statement */
  #followStop(id: number, attempt: number): void {
    this.#stopTimer = setTimeout(() => {
      this.#audit
        .execution(id)
        .pipe(catchError(() => of(null)), takeUntilDestroyed(this.#destroyRef))
        .subscribe((d) => {
          if (d && d.execution.status !== 'R') this.#stopEnded(id, null);
          else if (attempt >= 19) this.#stopEnded(id, 'audit.execution.stopNoAnswer');
          else this.#followStop(id, attempt + 1);
        });
    }, 1500);
  }

  #stopEnded(id: number, note: string | null): void {
    if (this.stoppingId() === id) this.stoppingId.set(null);
    this.stopNote.set(note ? { id, note } : null);
    if (this.selectedExecution()?.id === id) this.#detail$.next({ kind: 'execution', id, silent: true });
    this.#load$.next({ list: this.tab() === 'executions', summary: true, silent: true });
    this.cdr();
  }

  /** correlation: record changes of the session of an execution, around its run */
  executionChanges(e: AuditExecution): void {
    const w = executionChangesWindow(e.created, e.status === 'R' ? null : e.updated, new Date());
    this.closeDetail();
    this.patch({ ...clearAllFilters(this.query()), tab: 'changes', period: 'custom', from: w.from, to: w.to, sessionId: e.sessionId });
  }

  executionsOfProcess(e: AuditExecution): void {
    this.names.update((n) => ({ ...n, [`process:${e.processId}`]: e.processName }));
    this.closeDetail();
    this.patch({ ...clearAllFilters(this.query()), tab: 'executions', processId: e.processId });
  }

  byUser(tab: AuditTab, userId: number, userName: string | null): void {
    if (userName) this.names.update((n) => ({ ...n, [`user:${userId}`]: userName }));
    this.closeDetail();
    this.patch({ ...clearAllFilters(this.query()), tab, userId });
  }

  recordHistory(c: AuditChange): void {
    if (c.tableLabel || c.tableName) this.names.update((n) => ({ ...n, [`table:${c.tableId}`]: c.tableLabel || c.tableName }));
    this.closeDetail();
    this.patch({ ...clearAllFilters(this.query()), tab: 'changes', tableId: c.tableId, recordId: c.recordId, period: '90d', from: null, to: null });
  }

  sessionChanges(c: AuditChange): void {
    this.closeDetail();
    this.patch({ ...clearAllFilters(this.query()), tab: 'changes', sessionId: c.sessionId });
  }

  /** zoom to the record in its window (same path as notifications: access checked, menu entry required) */
  openRecord(c: AuditChange): void {
    if (!c.tableName || !c.keyColumn || !c.recordKey) return;
    this.uiCreator.zoomAcross(c.tableName, c.keyColumn, c.recordKey, true).subscribe({
      next: (dataWs) => {
        if (!dataWs?.length) {
          this.detailError.set(this.translateService.instant('audit.change.noAccess'));
          this.cdr();
          return;
        }
        this.uiCreator.getIupicsMenus().subscribe((menus) => {
          const windowId = parseInt(dataWs[0].Window_ID, 10);
          const menu = menus.find((m) => m.actionID === windowId && m.menuType === IupicsMenuType.WINDOW);
          if (!menu) {
            this.detailError.set(this.translateService.instant('audit.change.noAccess'));
            this.cdr();
            return;
          }
          this.workspaceService.openTargetSearchEmt.emit({
            zoomInfo: {
              windowId,
              dataUUID: dataWs[dataWs.length - 1]['Record_ID'],
              record_id: c.recordKey,
              children: dataWs.length > 1 ? dataWs.slice(0, dataWs.length - 1) : null,
            },
            cat: { id: windowId },
            // the first tab opens the root record (last entry); the others (children) are opened down to the record
            source: { id: dataWs[dataWs.length - 1]?.Record_ID ?? 'newRecord' },
          });
        });
      },
      error: () => {
        this.detailError.set(this.translateService.instant('audit.change.noAccess'));
        this.cdr();
      },
    });
  }
  //#endregion

  //#region display
  stale(e: AuditExecution): boolean {
    return isStale(e.status, e.created, this.now());
  }

  fieldsPreview(c: AuditChange): string {
    const labels = c.fields.filter((f) => f.kind !== 'note').map((f) => f.label || f.columnName || `#${f.columnId}`);
    return labels.slice(0, 3).join(', ');
  }

  /** value of an active filter chip */
  pillValue(key: AuditFilterKey): string {
    const f = this.query();
    const t = (k: string) => this.translateService.instant(k);
    switch (key) {
      case 'q':
        return `"${f.q.trim()}"`;
      case 'status':
        return f.status.map((s) => t(`audit.status.${s}`)).join(', ');
      case 'type':
        return f.type ? t(`audit.type.${f.type}`) : '';
      case 'processId':
        return this.nameOf('process', f.processId);
      case 'userId':
        return this.nameOf('user', f.userId);
      case 'tableId':
        return this.nameOf('table', f.tableId);
      case 'operation':
        return f.operation.map((o) => t(`audit.operation.${o}`)).join(', ');
      case 'recordId':
        return readableRecordId(f.recordId);
      case 'sessionId':
        return `#${f.sessionId}`;
      case 'action':
        return f.action.map((a) => t(`audit.docAction.${a}`)).join(', ');
    }
  }

  opKey(type: string): string {
    return this.operations.includes(type as AuditOperation) ? `audit.operation.${type}` : 'audit.operation.other';
  }

  /** an execution row or a change row selected (list highlight) */
  isSelected(id: number | string): boolean {
    return this.selectedExecution()?.id === id || this.selectedChange()?.id === id || this.selectedAccess()?.id === id;
  }
  //#endregion

  //#region export
  export(format: 'csv' | 'json'): void {
    if (this.exporting()) return;
    const f = this.query();
    this.exporting.set(true);
    this.#audit
      .export(f.tab, format, toApiParams(f, new Date()))
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `audit-${f.tab}-${toLocalDay(new Date())}.${format}`;
          a.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
          this.exporting.set(false);
          this.cdr();
        },
        error: async (e) => {
          // the error body of a blob request is a Blob: read it to get the message
          if (e instanceof HttpErrorResponse && e.error instanceof Blob) {
            try {
              const text = await e.error.text();
              e = new HttpErrorResponse({ error: JSON.parse(text), status: e.status, statusText: e.statusText, url: e.url ?? undefined });
            } catch {
              // not JSON: generic message
            }
          }
          this.exporting.set(false);
          this.listError.set(this.#message(e));
          this.cdr();
        },
      });
  }
  //#endregion

  #message(e: unknown): string {
    if (e instanceof HttpErrorResponse) {
      if (e.status === 403 || (e.status === 500 && /access denied/i.test(JSON.stringify(e.error ?? '')))) {
        return this.translateService.instant('audit.state.forbidden');
      }
      if (e.status === 0) return this.translateService.instant('audit.state.offline');
      const msg = e.error?.message;
      if (e.status === 400 && msg) return msg;
    }
    return this.translateService.instant('audit.state.error');
  }

  /** OnPush: data arrives from subscriptions and the live timer */
  private cdr(): void {
    this.#cd.markForCheck();
  }
}
